import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

type Accent = "blue" | "indigo" | "emerald" | "amber" | "rose";

const ACCENT_BAR: Record<Accent, string> = {
  blue: "bg-blue-500",
  indigo: "bg-indigo-500",
  emerald: "bg-emerald-500",
  amber: "bg-amber-500",
  rose: "bg-rose-500",
};

interface KpiCardProps {
  eticheta: string;
  valoare: string;
  subtext?: string;
  accent?: Accent;
  /** Slot opțional în dreapta valorii (ex. badge de deviație). */
  badge?: ReactNode;
}

/**
 * Card KPI premium — control total pe tipografie și overflow.
 * - `min-w-0` + `truncate` pe etichetă/subtext => textul nu iese din card.
 * - valoarea folosește `tabular-nums` și se micșorează controlat pe ecrane mici.
 */
export function KpiCard({ eticheta, valoare, subtext, accent = "blue", badge }: KpiCardProps) {
  return (
    <div className="relative flex min-w-0 flex-col overflow-hidden rounded-xl border border-slate-200/80 bg-white p-4 shadow-[0_1px_2px_0_rgb(15_23_42/0.04)]">
      <span className={cn("absolute inset-x-0 top-0 h-1", ACCENT_BAR[accent])} aria-hidden />
      <p
        className="mt-1 min-w-0 truncate text-[0.8125rem] font-medium leading-snug text-slate-500"
        title={eticheta}
      >
        {eticheta}
      </p>
      <div className="mt-1.5 flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="tabular text-[1.625rem] font-semibold leading-none tracking-tight text-slate-900">
          {valoare}
        </span>
        {badge}
      </div>
      {subtext && (
        <p className="mt-2 min-w-0 truncate text-xs text-slate-400" title={subtext}>
          {subtext}
        </p>
      )}
    </div>
  );
}
